import React, { useState, useEffect } from 'react';
import Nango from '@nangohq/frontend';
import { motion } from 'framer-motion';
import { nangoAPI } from '../lib/api';
import { useAuthStore } from '../store';
import { MetaApiHealthCard } from './MetaApiHealthCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Link2,
  Unlink,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RefreshCw,
  Calendar,
  MessageSquare,
  FileText,
  Database,
  Users,
  Zap,
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Available integrations
const AVAILABLE_INTEGRATIONS = [
  {
    id: 'facebook',
    name: 'Facebook & Instagram',
    description: 'Gestiona páginas, publica contenido, responde mensajes y comentarios',
    icon: MessageSquare,
    color: 'bg-blue-600',
    category: 'Redes Sociales',
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Recibe notificaciones y envía mensajes',
    icon: MessageSquare,
    color: 'bg-purple-500',
    category: 'Comunicación',
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Sincroniza eventos y reuniones',
    icon: Calendar,
    color: 'bg-blue-500',
    category: 'Productividad',
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    description: 'Accede y gestiona archivos',
    icon: FileText,
    color: 'bg-yellow-500',
    category: 'Almacenamiento',
  },
  {
    id: 'notion',
    name: 'Notion',
    description: 'Sincroniza bases de datos y páginas',
    icon: Database,
    color: 'bg-gray-800',
    category: 'Productividad',
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Integra tu CRM y contactos',
    icon: Users,
    color: 'bg-orange-500',
    category: 'CRM',
  },
];

export function IntegrationsPage() {
  const { user } = useAuthStore();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [error, setError] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const data = await nangoAPI.getConnections();
      setConnections(data.connections || []);
    } catch (err) {
      console.error('Error loading connections:', err);
      setError('Error al cargar las integraciones');
    } finally {
      setLoading(false);
    }
  };

  const getSessionToken = async (integrationId) => {
    try {
      const data = await nangoAPI.createSession([integrationId]);
      return data;
    } catch (err) {
      console.error('Error creating session:', err);
      throw new Error('No se pudo crear la sesión de autenticación');
    }
  };

  const handleConnect = async (integration) => {
    setConnecting(integration.id);
    setError(null);

    try {
      const sessionData = await getSessionToken(integration.id);
      
      if (!sessionData.token) {
        throw new Error('No se pudo crear la sesión');
      }
      
      // Use connect_link for more reliable popup
      if (sessionData.connect_link) {
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          sessionData.connect_link,
          'nango_connect',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
        );
        
        if (!popup) {
          throw new Error('El popup fue bloqueado. Por favor permite los popups para este sitio.');
        }
        
        // Poll for popup close
        const pollInterval = setInterval(async () => {
          if (popup.closed) {
            clearInterval(pollInterval);
            setConnecting(null);
            setSessionToken(null);
            await loadConnections();
          }
        }, 1000);
        
        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setConnecting(null);
        }, 300000);
        
      } else {
        // Fallback to SDK
        const nango = new Nango({ connectSessionToken: sessionData.token });
        const result = await nango.auth(integration.id);
        
        if (result && result.connectionId) {
          await nangoAPI.storeConnection(integration.id, result.connectionId);
          await loadConnections();
        }
        setConnecting(null);
        setSessionToken(null);
      }
    } catch (err) {
      console.error('Connection error:', err);
      setConnecting(null);
      if (err.message?.includes('window_closed')) {
        setError('La ventana de autorización fue cerrada');
      } else if (err.message?.includes('blocked') || err.message?.includes('popup')) {
        setError('El popup fue bloqueado por el navegador. Por favor permite los popups.');
      } else {
        setError(`Error al conectar: ${err.message || 'Intenta de nuevo'}`);
      }
      setSessionToken(null);
    }
  };

  const handleDisconnect = async (integration) => {
    setConnecting(integration.id);
    setError(null);

    try {
      await nangoAPI.disconnect(integration.id);
      await loadConnections();
    } catch (err) {
      console.error('Disconnect error:', err);
      setError(`Error al desconectar: ${err.message}`);
    } finally {
      setConnecting(null);
    }
  };

  const isConnected = (integrationId) => {
    return connections.some(c => c.provider_config_key === integrationId || c.integration_id === integrationId);
  };

  const getConnection = (integrationId) => {
    return connections.find(c => c.provider_config_key === integrationId || c.integration_id === integrationId);
  };

  // Group integrations by category
  const groupedIntegrations = AVAILABLE_INTEGRATIONS.reduce((acc, integration) => {
    if (!acc[integration.category]) {
      acc[integration.category] = [];
    }
    acc[integration.category].push(integration);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="integrations-page"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Integraciones</h1>
          <p className="text-muted-foreground mt-1">
            Conecta tus herramientas favoritas con Portal Karma Digital
          </p>
        </div>
        <Button variant="outline" onClick={loadConnections} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </motion.div>

      {user?.role === 'ceo' && <motion.div variants={itemVariants}><MetaApiHealthCard /></motion.div>}

      {/* Error Alert */}
      {error && (
        <motion.div variants={itemVariants}>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700">{error}</p>
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                Cerrar
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Connected Count */}
      <motion.div variants={itemVariants} className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-emerald-100">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{connections.length}</p>
              <p className="text-sm text-muted-foreground">Conectadas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100">
              <Zap className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{AVAILABLE_INTEGRATIONS.length}</p>
              <p className="text-sm text-muted-foreground">Disponibles</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-purple-100">
              <Link2 className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{AVAILABLE_INTEGRATIONS.length - connections.length}</p>
              <p className="text-sm text-muted-foreground">Por conectar</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Integrations by Category */}
      {Object.entries(groupedIntegrations).map(([category, integrations]) => (
        <motion.div key={category} variants={itemVariants}>
          <h2 className="text-lg font-semibold mb-4">{category}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {integrations.map((integration) => {
              const connected = isConnected(integration.id);
              const connection = getConnection(integration.id);
              const Icon = integration.icon;
              const isProcessing = connecting === integration.id;

              return (
                <Card key={integration.id} className="card-hover">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-lg ${integration.color}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      {connected && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Conectado
                        </Badge>
                      )}
                    </div>

                    <h3 className="font-semibold text-lg mb-1">{integration.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{integration.description}</p>

                    {connected && connection?.stored_at && (
                      <p className="text-xs text-muted-foreground mb-4">
                        Conectado: {new Date(connection.stored_at).toLocaleDateString('es-ES')}
                      </p>
                    )}

                    {connected ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleDisconnect(integration)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Desconectando...
                          </>
                        ) : (
                          <>
                            <Unlink className="w-4 h-4 mr-2" />
                            Desconectar
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        className="w-full gradient-primary text-white"
                        onClick={() => handleConnect(integration)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Conectando...
                          </>
                        ) : (
                          <>
                            <Link2 className="w-4 h-4 mr-2" />
                            Conectar
                          </>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}
